import React, { useMemo, useState } from 'react';

function hasTagOverlap(allowedTags, memberTags) {
  if (!Array.isArray(allowedTags) || allowedTags.length === 0) return true;
  const mine = new Set(memberTags || []);
  return allowedTags.some((tag) => mine.has(tag));
}

function normalizeSearch(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function getMemberSearchText(member) {
  return [
    member?.name,
    member?.firstName,
    member?.lastName,
    member?.username,
    member?.id,
  ]
    .filter((value) => typeof value === 'string' && value.trim() !== '')
    .join(' ')
    .toLowerCase();
}

export default function RolesReadOnly({
  roles = [],
  isAdmin = false,
  isTeamMember = false,
  currentUserId = '',
  memberTagNames = [],
  myRequestsBySlot = {},
  myRequestsByScopeSlot = {},
  pendingRequests = [],
  memberOptions = [],
  onVolunteer,
  onWithdraw,
  onApprove,
  onReject,
  onDutyResponse,
  onSwapRequest,
  onAssign,
  onClear,
  busy = false,
  coverageScope = 'event',
  coverageHint = '',
  allowScopeChoice = false,
}) {
  const [assignBySlot, setAssignBySlot] = useState({});
  const [searchBySlot, setSearchBySlot] = useState({});

  const pendingBySlot = useMemo(() => {
    return (pendingRequests || []).reduce((acc, req) => {
      const key = req.slotId;
      if (!key) return acc;
      acc[key] = acc[key] || [];
      acc[key].push(req);
      return acc;
    }, {});
  }, [pendingRequests]);

  if (!roles.length) return null;
  const canChooseScope = allowScopeChoice && coverageScope === 'series';

  return (
    <div className="card" style={{ padding: 16, borderRadius: 12, border: '1px solid #eee', marginBottom: 12 }}>
      <h3 style={{ marginTop: 0 }}>Event Roles</h3>
      {coverageHint && (
        <p className="text-muted" style={{ marginTop: -6, marginBottom: 10 }}>
          {coverageHint}
        </p>
      )}
      {busy && (
        <p
          className="text-muted"
          role="status"
          aria-live="polite"
          style={{ marginTop: -6, marginBottom: 10 }}
        >
          Updating role assignment...
        </p>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {roles.map((role, roleIndex) => {
          const allowedTags = Array.isArray(role.allowedTags) ? role.allowedTags : [];
          const slots = Array.isArray(role.slots) ? role.slots : [];

          return (
            <div key={role.id || `role:${roleIndex}`} className="card" style={{ padding: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{role.name || 'Unnamed role'}</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    {role.requiresApproval === false
                      ? 'First come, first serve'
                      : 'Admin approval required'}
                  </div>
                  {allowedTags.length > 0 && (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Eligible tags: {allowedTags.join(', ')}
                    </div>
                  )}
                </div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {slots.length} slot{slots.length === 1 ? '' : 's'}
                </div>
              </div>

              <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                {slots.map((slot, slotIndex) => {
                  const slotKey = slot.id || `${role.id || roleIndex}:slot:${slotIndex}`;
                  const isFilled = !!slot.assigneeUserId;
                  const myReq = myRequestsBySlot[slotKey] || null;
                  const myReqOccurrence = myRequestsByScopeSlot[`occurrence:${slotKey}`] || null;
                  const myReqSeries = myRequestsByScopeSlot[`series:${slotKey}`] || null;
                  const eligible = hasTagOverlap(allowedTags, memberTagNames);
                  const slotPending = pendingBySlot[slotKey] || [];
                  const slotSearch = searchBySlot[slotKey] || '';
                  const normalizedSlotSearch = normalizeSearch(slotSearch);
                  const filteredMembers = normalizedSlotSearch
                    ? memberOptions.filter((member) =>
                        getMemberSearchText(member).includes(normalizedSlotSearch)
                      )
                    : memberOptions;
                  const selectedMemberId = assignBySlot[slotKey] || '';
                  const selectedRequest = slotPending.find(
                    (req) => req.requestedBy === selectedMemberId
                  );
                  const isMyDuty = Boolean(currentUserId && slot.assigneeUserId === currentUserId);
                  const responseStatus = String(slot.responseStatus || '').toLowerCase();
                  const responseLabel =
                    responseStatus === 'accepted'
                      ? 'Accepted'
                      : responseStatus === 'declined'
                        ? 'Declined'
                        : responseStatus === 'pending'
                          ? 'Needs response'
                          : '';

                  return (
                    <div key={slotKey} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div>
                          <strong>Slot {slotIndex + 1}:</strong>{' '}
                          {isFilled
                            ? `${slot.assigneeDisplayName || 'Member'}`
                            : 'Vacant'}
                          {responseLabel && (
                            <span className="text-muted" style={{ marginLeft: 6 }}>
                              ({responseLabel})
                            </span>
                          )}
                        </div>

                        {!isFilled && isTeamMember && !isAdmin && (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            {!eligible ? (
                              <span className="text-muted">You are not eligible for this role.</span>
                            ) : canChooseScope ? (
                              role.requiresApproval === false ? (
                                <>
                                  <button
                                    type="button"
                                    className="button button--xs"
                                    onClick={() => onVolunteer?.({ role, slot, slotKey, scope: 'occurrence' })}
                                  >
                                    Take role this event
                                  </button>
                                  <button
                                    type="button"
                                    className="button button--xs"
                                    onClick={() => onVolunteer?.({ role, slot, slotKey, scope: 'series' })}
                                  >
                                    Take role series
                                  </button>
                                </>
                              ) : (
                                <>
                                  {myReqOccurrence?.status === 'requested' ? (
                                    <>
                                      <span className="text-muted">This event pending</span>
                                      <button
                                        type="button"
                                        className="button button--xs"
                                        onClick={() => onWithdraw?.(myReqOccurrence)}
                                      >
                                        Withdraw
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="button button--xs"
                                      onClick={() => onVolunteer?.({ role, slot, slotKey, scope: 'occurrence' })}
                                    >
                                      Volunteer this event
                                    </button>
                                  )}

                                  {myReqSeries?.status === 'requested' ? (
                                    <>
                                      <span className="text-muted">Series pending</span>
                                      <button
                                        type="button"
                                        className="button button--xs"
                                        onClick={() => onWithdraw?.(myReqSeries)}
                                      >
                                        Withdraw
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="button button--xs"
                                      onClick={() => onVolunteer?.({ role, slot, slotKey, scope: 'series' })}
                                    >
                                      Volunteer series
                                    </button>
                                  )}
                                </>
                              )
                            ) : role.requiresApproval !== false && myReq?.status === 'requested' ? (
                              <>
                                <span className="text-muted">Request pending</span>
                                <button
                                  type="button"
                                  className="button button--xs"
                                  onClick={() => onWithdraw?.(myReq)}
                                >
                                  Withdraw
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="button button--xs"
                                onClick={() => onVolunteer?.({ role, slot, slotKey })}
                              >
                                {role.requiresApproval === false
                                  ? (coverageScope === 'series' ? 'Take role (series)' : 'Take role')
                                  : (coverageScope === 'series' ? 'Volunteer for series' : 'Volunteer')}
                              </button>
                            )}
                          </div>
                        )}

                        {isFilled && isAdmin && (
                          canChooseScope ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="button button--xs"
                                onClick={() => onClear?.({ role, slot, slotKey, scope: 'occurrence' })}
                              >
                                Clear just this event
                              </button>
                              <button
                                type="button"
                                className="button button--xs"
                                onClick={() => onClear?.({ role, slot, slotKey, scope: 'series' })}
                              >
                                Clear series assignment
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="button button--xs"
                              onClick={() => onClear?.({ role, slot, slotKey })}
                            >
                              {coverageScope === 'series' ? 'Clear series assignment' : 'Clear assignment'}
                            </button>
                          )
                        )}

                        {isFilled && isMyDuty && !isAdmin && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {responseStatus !== 'accepted' && (
                              <button
                                type="button"
                                className="button button--xs"
                                onClick={() => onDutyResponse?.({ role, slot, slotKey, status: 'accepted' })}
                              >
                                Accept
                              </button>
                            )}
                            {responseStatus !== 'declined' && (
                              <button
                                type="button"
                                className="button button--xs"
                                onClick={() => onDutyResponse?.({ role, slot, slotKey, status: 'declined' })}
                              >
                                Decline
                              </button>
                            )}
                            <button
                              type="button"
                              className="button button--xs"
                              onClick={() => onSwapRequest?.({ role, slot, slotKey })}
                            >
                              Request swap
                            </button>
                          </div>
                        )}
                      </div>

                      {!isFilled && !isTeamMember && !isAdmin && (
                        <p className="text-muted" style={{ margin: '6px 0 0' }}>
                          Join this team to volunteer for vacant roles.
                        </p>
                      )}

                      {isAdmin && !isFilled && (
                        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                          <div className="text-muted" style={{ marginBottom: -2 }}>
                            {canChooseScope
                              ? 'Assign member to this event or the full series'
                              : (coverageScope === 'series' ? 'Assign member (series coverage)' : 'Assign member')}
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              className="input"
                              type="search"
                              value={slotSearch}
                              placeholder="Search first name, surname, username"
                              onChange={(e) =>
                                setSearchBySlot((prev) => ({
                                  ...prev,
                                  [slotKey]: e.target.value,
                                }))
                              }
                              style={{ minWidth: 220 }}
                            />
                            <select
                              className="input"
                              value={selectedMemberId}
                              onChange={(e) =>
                                setAssignBySlot((prev) => ({ ...prev, [slotKey]: e.target.value }))
                              }
                              style={{ minWidth: 220 }}
                            >
                              <option value="">
                                {filteredMembers.length === 0 ? 'No matches found' : 'Select member'}
                              </option>
                              {filteredMembers.map((member) => (
                                <option key={`${slotKey}:${member.id}`} value={member.id}>
                                  {member.name}
                                </option>
                              ))}
                            </select>
                            {canChooseScope ? (
                              <>
                                <button
                                  type="button"
                                  className="button button--xs"
                                  disabled={!selectedMemberId}
                                  onClick={() => {
                                    const memberId = selectedMemberId;
                                    if (!memberId) return;
                                    onAssign?.({ role, slot, slotKey, memberId, scope: 'occurrence' });
                                  }}
                                >
                                  Assign to just this event
                                </button>
                                <button
                                  type="button"
                                  className="button button--xs"
                                  disabled={!selectedMemberId}
                                  onClick={() => {
                                    const memberId = selectedMemberId;
                                    if (!memberId) return;
                                    onAssign?.({ role, slot, slotKey, memberId, scope: 'series' });
                                  }}
                                >
                                  Assign to series
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                className="button button--xs"
                                disabled={!selectedMemberId}
                                onClick={() => {
                                  const memberId = selectedMemberId;
                                  if (!memberId) return;
                                  onAssign?.({ role, slot, slotKey, memberId });
                                }}
                              >
                                {coverageScope === 'series' ? 'Assign to series' : 'Assign'}
                              </button>
                            )}
                          </div>

                          {role.requiresApproval !== false && slotPending.length > 0 && (
                            <div>
                              <div className="text-muted" style={{ marginBottom: 4 }}>Pending volunteer offers</div>
                              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                                {slotPending.map((req) => (
                                  <li key={req.id} style={{ marginBottom: 4 }}>
                                    {(req.requestedByName || 'Member')}
                                    {canChooseScope && (
                                      <span className="text-muted" style={{ marginLeft: 6 }}>
                                        (
                                        {req.scope === 'occurrence' ? 'this event' : 'series'}
                                        )
                                      </span>
                                    )}
                                    <span style={{ marginLeft: 8, display: 'inline-flex', gap: 6 }}>
                                      <button type="button" className="button button--xs" onClick={() => onApprove?.(req)}>
                                        Approve
                                      </button>
                                      <button type="button" className="button button--xs" onClick={() => onReject?.(req)}>
                                        Reject
                                      </button>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {role.requiresApproval !== false && selectedMemberId && !selectedRequest && (
                            <p className="text-muted" style={{ margin: 0 }}>
                              This member has not volunteered for this slot yet.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
